<script setup lang="ts">
import { ref, type PropType, nextTick } from 'vue';
import { type FormField } from '@/models/form';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

const props = defineProps({
  fields: {
    type: Object as PropType<FormField[]>,
    required: true
  },
  previewMode: {
    type: Boolean
  },
  newEntry: {
    type: Boolean
  }
});

const searchInputVisibility = ref(false);
const searchText = ref();
const filteredFields = ref<FormField[]>([]);
const emits = defineEmits(['onFieldSelected']);

const onInput = () => {
  if (searchText.value !== '') {
    filteredFields.value = props.fields.filter(field => field.label.toLowerCase().includes(searchText.value.toLowerCase()) && field.show !== false);
  } else {
    filteredFields.value = [];
  }
};

const onFieldClick = (formField: FormField) => {
  const index = props.fields.findIndex(field => field.identifier == formField.identifier);
  emits('onFieldSelected', index);
  filteredFields.value = [];
  searchText.value = '';
  onSearchIconClick();
};

const onSearchIconClick = async () => {
  searchInputVisibility.value = !searchInputVisibility.value;
  if (!searchInputVisibility.value) {
    filteredFields.value = [];
    searchText.value = '';
  } else {
    await nextTick();
    const searchElement = document.querySelector('#search') as HTMLElement;
    searchElement?.focus();
  }
};

</script>

<template>
    <div class="root">
        <div class="search">
            <InputText id="search"
                v-model="searchText"
                type="text"
                @input="onInput"
                placeholder="Search field by label"
                v-if="searchInputVisibility"
                :class="{ 'animate-in': searchInputVisibility }"
                class="field-search-input"/>
            <Button icon="pi pi-search"
                outlined
                class="search-button"
                @click="onSearchIconClick()"
                v-ripple/>
        </div>
        <div class="field-list" :class="{'fade-in': filteredFields.length > 0}" v-if="filteredFields.length > 0">
            <div v-for="field in filteredFields" :key="field.identifier">
                <div class="field-row" @click="onFieldClick(field)" v-ripple>
                    {{ field.label }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.root {
    padding: 2px 80px;
    z-index: 1;
    position: absolute;
    width: 100%;
    bottom: -55px;
    right: 0;
    left: 0;

    .search {
        width: 100%;
        display: flex;
        justify-content: end;

        .field-search-input {
            flex: 1;
            margin-right: 16px;

            top: -200px;
            transform: translateX(-50%);
            transition: top 0.5s ease; 
        }

        .search-button {
            top: 0px;
            background-color: white;
        }
    }

    .field-list {
        opacity: 0;
        transition: opacity 0.5s ease;
        z-index: 1;
        padding: 12px;
        background-color: #e6edf8;
        box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
          0 1px 3px 1px rgba(15, 15, 15, 0.05);
        margin-top: 4px;
        margin-right: 56px;
        border-radius: 4px;

        .field-row {
            padding: 4px;
            font-weight: 500;
            cursor: pointer;

            &:hover {
                background-color: #b6cff7;
                border-radius: 4px;
            }
        }
    }

    .fade-in {
        opacity: 1;
    }
}

.animate-in {
  animation: bounceDown 0.5s cubic-bezier(0.17, 0.67, 0.83, 0.67) forwards;
}

@keyframes bounceDown {
  0% {
    transform: translateY(-150%);
  }
  70% {
    transform: translateY(0);
  }
  85% {
    transform: translateY(-15px);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
